// BotConnector.js
import React, { useMemo, useRef, useState } from 'react';
import axios from '../utils/axiosConfig';
import { useToast } from '@chakra-ui/react';
import {
    IconButton,
    Box,
    Button,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Text,
    useColorMode,
    useDisclosure
} from '@chakra-ui/react';
import { FaMicrophone, FaMicrophoneSlash } from 'react-icons/fa';
import { NativeWebCallClient } from "./NativeWebCallClient.js";
import { useAssistant } from "../context/AssistantContext";
import CallSetupFields, {
    buildCallData,
    extractMustacheVariables,
    readStoredCallPhones,
    readStoredCallVariables,
    saveStoredCallPhones,
    saveStoredCallVariables
} from './rightpanel/CallSetupFields';
import LiveCallActivityTimeline from './LiveCallActivityTimeline';

export default function BotConnector({ assistantId }) {
    const toast = useToast();
    const { colorMode } = useColorMode();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { formState } = useAssistant();

    const [botRunning, setBotRunning] = useState(false);
    const webCallClientRef = useRef(null);
    const [userSpeaking, setUserSpeaking] = useState(false);
    const [botSpeaking, setBotSpeaking] = useState(false);

    // idle | connecting | connected
    const [connectionStatus, setConnectionStatus] = useState("idle"); 
    const [micEnabled, setMicEnabled] = useState(true);
    const [participantPhone, setParticipantPhone] = useState('');
    const [variableValues, setVariableValues] = useState({});
    const [liveActivityUrl, setLiveActivityUrl] = useState('');

    const isRealtimeModel = (model = '') => {
        return [
            'realtime:',
            'realtime-audio:',
            'realtime-text-audio:',
            'realtime-audio-text:'
        ].some(prefix => model.startsWith(prefix));
    };

    const callVariables = useMemo(() => {
        return extractMustacheVariables(formState.firstMessage, formState.systemPrompt);
    }, [formState.firstMessage, formState.systemPrompt]);

    const loadStoredSetup = () => {
        const storedPhones = readStoredCallPhones(assistantId);
        const storedVariables = readStoredCallVariables(assistantId);

        setParticipantPhone(storedPhones.to || '');
        setVariableValues(
            callVariables.reduce((values, variable) => ({
                ...values,
                [variable]: storedVariables[variable] ?? variableValues[variable] ?? ''
            }), {})
        );
    };

    const handleParticipantPhoneChange = (value) => {
        setParticipantPhone(value);
        saveStoredCallPhones(assistantId, {
            ...readStoredCallPhones(assistantId),
            to: value
        });
    };

    const handleVariableValueChange = (variable, value) => {
        setVariableValues(prev => {
            const nextValues = {
                ...prev,
                [variable]: value
            };

            saveStoredCallVariables(assistantId, nextValues);
            return nextValues;
        });
    };


    const getVoiceDomain = async () => {
        const response = await axios.get('/api/protected/settings');

        if (response.data.success) {
            const { voiceDomainName } = response.data.data;
            return voiceDomainName;
        } else {
            toast({
                title: 'Error',
                description: 'Failed to fetch settings',
                status: 'error',
                duration: 5000,
                isClosable: true,
                variant: 'solid'
            });
        }
        return "";
    }

    const initBot = async (callSetup = {}) => {
        const voiceDomain = await getVoiceDomain();
        const url = `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${voiceDomain}/connection`;
        console.log('connecting to bot at:', url);

        const response = await axios.post(`/api/protected/assistants/${assistantId}/callToken`, callSetup);
        if (!response.data.success) {
            throw new Error('Failed to generate call token');
        }

        const token = response.data.token;
        const callSid = response.data.callSid || "webcall";

        if (isRealtimeModel(formState.model) && response.data.liveActivityUrl) {
            setLiveActivityUrl(response.data.liveActivityUrl);
        }

        const client = new NativeWebCallClient({
            wsUrl: url,
            onStateChange: (state) => {
                if (state === 'connecting') {
                    setConnectionStatus("connecting");
                } else if (state === 'connected') {
                    setBotRunning(true);
                    setConnectionStatus("connected");
                } else if (state === 'idle') {
                    setBotRunning(false);
                    setBotSpeaking(false);
                    setUserSpeaking(false);
                    setConnectionStatus("idle");
                }
            },
            onUserSpeakingChange: setUserSpeaking,
            onBotSpeakingChange: setBotSpeaking,
            onError: (error) => {
                console.error('Web call error:', error);
            }
        });
        webCallClientRef.current = client;
        await client.connect({ token, callSid });
    }

    const handleToggleBot = async () => {
        const client = webCallClientRef.current;

        if (botRunning) {
            if (client) {
                await client.disconnect();
                webCallClientRef.current = null;
                setMicEnabled(true); // Reset mic state
            }
        } else {
            loadStoredSetup();
            onOpen();
        }
    };

    const handleStartWebCall = async () => {
        try {
            onClose();
            await initBot({
                phone: participantPhone,
                callParticipant: participantPhone,
                data: buildCallData(variableValues),
                debug: {
                    liveActivity: true
                }
            });
        } catch (error) {
            console.error("Error connecting to bot:", error);
            toast({
                title: "Connection Error",
                description: "Failed to connect to the bot. Please try again. Error: " + error.message,
                status: "error",
                duration: 4000,
                isClosable: true,
            });
        }
    };

    const toggleMicMute = () => {
        const client = webCallClientRef.current;

        if (botRunning) {
            if (!client) return;

            const nextMicEnabled = !client.isMicEnabled;

            client.setMicEnabled(nextMicEnabled);
            setMicEnabled(nextMicEnabled);
        }

    }

    return (
        <Box textAlign="center" >
            { connectionStatus === "connected" && (

                <IconButton
                    icon={micEnabled ? <FaMicrophone /> : <FaMicrophoneSlash />}
                    onClick={toggleMicMute}
                    colorScheme={botRunning ? 'red' : 'gray'}
                    // borderRadius="full"
                    boxShadow={userSpeaking ? '0 0 20px red' : botSpeaking ? '0 0 20px yellow' :'none'}
                    className={userSpeaking || botSpeaking? 'pulse' : ''}
                    aria-label="Toggle mic"
                    mr={1}
                />
            )}
            <Button 
                leftIcon={botRunning ? null : <FaMicrophone />}
                colorScheme="blue"
                onClick={handleToggleBot}>
                {connectionStatus === "connecting" 
                    ? "Connecting..." 
                    : connectionStatus === "connected" 
                    ? "Hang Up" 
                    : "Talk to Assistant"}
            </Button>
            <Modal isOpen={!!liveActivityUrl} onClose={() => setLiveActivityUrl('')} size="xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Talk to Assistant - Live Activity</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <LiveCallActivityTimeline
                            liveActivityUrl={liveActivityUrl}
                            onStopMonitoring={() => setLiveActivityUrl('')}
                        />
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" onClick={() => setLiveActivityUrl('')}>Close</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
            <Modal isOpen={isOpen} onClose={onClose} size="xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Talk to Assistant</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {!isRealtimeModel(formState.model) && (
                            <Text fontSize="sm" color={colorMode === 'light' ? 'gray.600' : 'gray.300'} mb={4}>
                                Live activity is available for Realtime models only.
                            </Text>
                        )}
                        <CallSetupFields
                            colorMode={colorMode}
                            showParticipantPhone
                            participantPhone={participantPhone}
                            onParticipantPhoneChange={handleParticipantPhoneChange}
                            participantPhonePlaceholder="Enter phone number for context"
                            variables={callVariables}
                            variableValues={variableValues}
                            onVariableChange={handleVariableValueChange}
                        />
                    </ModalBody>
                    <ModalFooter>
                        <Button colorScheme="blue" onClick={handleStartWebCall}>
                            Start
                        </Button>
                        <Button variant="ghost" onClick={onClose}>Cancel</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
         
        </Box>
    )
}
